import { useCallback, useEffect, useRef, useState } from "react";
import { Film, Key, Loader2, Search } from "lucide-react";
import { Modal } from "../../components/Modal";
import { Button, Field, Input, Select, Textarea } from "../../components/ui";
import ClockTimePicker from "../../components/ClockTimePicker";
import {
  addMovie, updateMovie, setMovieStatus,
  issueKeys, allocateKeys, revokeKeys, returnKeys, consumeKeys,
} from "../../api/movieKeys";
import { listBranches } from "../../api/branches";
import { listEmployees } from "../../api/employees";
import { useAuth } from "../../context/AuthContext";
import { fieldErrors, errorMessage, unwrapList } from "../../lib/records";

const FORMATS = ["2D", "3D", "4DX", "IMAX", "IMAX 3D", "4K HDR"];
const CERTIFICATIONS = ["U", "UA", "A", "S"];

export function AddFilmModal({ open, film, onClose, onDone }) {
  const isEdit = !!film?._id;
  const [title, setTitle] = useState("");
  const [language, setLanguage] = useState("");
  const [format, setFormat] = useState("");
  const [certification, setCertification] = useState("");
  const [distributor, setDistributor] = useState("");
  const [releaseDate, setReleaseDate] = useState("");
  const [note, setNote] = useState("");
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(film?.title ?? "");
    setLanguage(film?.language ?? "");
    setFormat(film?.format ?? "");
    setCertification(film?.certification ?? "");
    setDistributor(film?.distributor ?? "");
    setReleaseDate(film?.releaseDate?.slice(0, 10) ?? "");
    setNote("");
    setErrors({});
    setFormError("");
  }, [open, film]);

  async function submit(e) {
    e.preventDefault();
    setErrors({});
    setFormError("");
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        ...(language.trim() ? { language: language.trim() } : {}),
        ...(format ? { format } : {}),
        ...(certification ? { certification } : {}),
        ...(distributor.trim() ? { distributor: distributor.trim() } : {}),
        ...(releaseDate ? { releaseDate } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      };
      if (isEdit) await updateMovie(film._id, payload);
      else await addMovie(payload);
      onDone();
    } catch (err) {
      const mapped = fieldErrors(err);
      if (mapped) setErrors(mapped);
      else setFormError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit film" : "Add film"}
      description="Films are shared across the chain — all branches will see them."
    >
      <form onSubmit={submit} className="space-y-4">
        {formError && (
          <div className="rounded-md bg-red-50 border border-red-200 px-3.5 py-3 text-sm text-red-700">
            {formError}
          </div>
        )}

        <Field label="Title" required error={errors.title}>
          <Input
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Interstellar"
            className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
          />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
          <Field label="Language" error={errors.language}>
            <Input
              value={language}
              onChange={e => setLanguage(e.target.value)}
              placeholder="Hindi"
              className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
            />
          </Field>
          <Field label="Format" error={errors.format}>
            <Select
              value={format}
              onChange={e => setFormat(e.target.value)}
              className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
            >
              <option value="">Select format</option>
              {FORMATS.map(f => <option key={f} value={f}>{f}</option>)}
            </Select>
          </Field>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
          <Field label="Certification" error={errors.certification}>
            <Select
              value={certification}
              onChange={e => setCertification(e.target.value)}
              className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
            >
              <option value="">Select</option>
              {CERTIFICATIONS.map(c => <option key={c} value={c}>{c}</option>)}
            </Select>
          </Field>
          <Field label="Release date" error={errors.releaseDate}>
            <Input
              type="date"
              value={releaseDate}
              onChange={e => setReleaseDate(e.target.value)}
              className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
            />
          </Field>
        </div>

        <Field label="Distributor" error={errors.distributor}>
          <Input
            value={distributor}
            onChange={e => setDistributor(e.target.value)}
            placeholder="PVR Pictures"
            className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
          />
        </Field>

        <Field label="Note" error={errors.note}>
          <Textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            rows={2}
            placeholder="Optional notes"
            className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
          />
        </Field>

        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2.5 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} className="text-gray-600 w-full sm:w-auto">
            Cancel
          </Button>
          <Button
            type="submit"
            loading={saving}
            className="bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-lg hover:shadow-emerald-500/25 w-full sm:w-auto"
          >
            <Film size={15} /> {isEdit ? "Save changes" : "Add film"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function IssueModal({ open, film, onClose, onDone }) {
  const [keys, setKeys] = useState("1");
  const [note, setNote] = useState("");
  const [clientRef, setClientRef] = useState("");
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setKeys("1");
    setNote("");
    setClientRef("");
    setErrors({});
    setFormError("");
  }, [open]);

  async function submit(e) {
    e.preventDefault();
    setErrors({});
    setFormError("");
    setSaving(true);
    try {
      await issueKeys({
        movie: film._id,
        keys: Number(keys) || 1,
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(clientRef.trim() ? { clientRef: clientRef.trim() } : {}),
      });
      onDone();
    } catch (err) {
      const mapped = fieldErrors(err);
      if (mapped) setErrors(mapped);
      else setFormError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Issue keys"
      description={`Declare received keys for "${film?.title ?? ""}". Keys are added to your chain holding.`}
      maxWidth="max-w-md"
    >
      <form onSubmit={submit} className="space-y-4">
        {formError && (
          <div className="rounded-md bg-red-50 border border-red-200 px-3.5 py-3 text-sm text-red-700">
            {formError}
          </div>
        )}

        <Field label="Number of keys" required error={errors.keys}>
          <Input
            value={keys}
            onChange={e => setKeys(e.target.value.replace(/\D/g, ""))}
            inputMode="numeric"
            placeholder="1"
            className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
          />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
          <Field label="Note" error={errors.note}>
            <Input
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="From distributor"
              className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
            />
          </Field>
          <Field label="Reference" error={errors.clientRef} hint="Your tracking ID">
            <Input
              value={clientRef}
              onChange={e => setClientRef(e.target.value)}
              placeholder="KDM-2024-001"
              className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
            />
          </Field>
        </div>

        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2.5 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} className="text-gray-600 w-full sm:w-auto">
            Cancel
          </Button>
          <Button
            type="submit"
            loading={saving}
            className="bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:shadow-lg hover:shadow-blue-500/25 w-full sm:w-auto"
          >
            <Key size={15} /> Issue {keys || "0"} key{Number(keys) !== 1 ? "s" : ""}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

const EMP_LIMIT = 10;

function EmployeePicker({ value, onChange, branchId, isChain }) {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const sentinel = useRef(null);
  const listRef = useRef(null);

  const fetchPage = useCallback(async (p, reset = false) => {
    setLoading(true);
    try {
      const params = { page: p, limit: EMP_LIMIT };
      if (isChain && branchId) params.partner = branchId;
      if (search.trim()) params.search = search.trim();
      const res = await listEmployees(params);
      const { items: newItems, pagination } = unwrapList(res, EMP_LIMIT);
      setItems(prev => reset ? newItems : [...prev, ...newItems]);
      setPage(p);
      setHasMore(p < (pagination.pages ?? 1));
    } catch { /* silenced */ }
    finally { setLoading(false); }
  }, [branchId, isChain, search]);

  useEffect(() => { fetchPage(1, true); }, [fetchPage]);

  useEffect(() => {
    if (!sentinel.current) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && hasMore && !loading) fetchPage(page + 1);
    }, { root: listRef.current, threshold: 0.5 });
    observer.observe(sentinel.current);
    return () => observer.disconnect();
  }, [hasMore, loading, page, fetchPage]);

  const selected = items.find(i => i._id === value);

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search employees…"
          className="w-full rounded-lg border border-gray-200 pl-8 pr-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
        />
      </div>
      {selected && (
        <div className="flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-500 text-[11px] font-bold text-white">
            {selected.name?.[0] ?? "?"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-emerald-900 truncate">{selected.name}</p>
            {selected.designation && <p className="text-[11px] text-emerald-600">{selected.designation}</p>}
          </div>
          <button type="button" onClick={() => onChange("")} className="text-emerald-400 hover:text-emerald-700 text-xs font-medium">
            Change
          </button>
        </div>
      )}
      <div ref={listRef} className="max-h-48 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-50">
        {items.length === 0 && !loading && (
          <p className="py-6 text-center text-sm text-gray-400">No employees found</p>
        )}
        {items.map(emp => (
          <button
            key={emp._id}
            type="button"
            onClick={() => onChange(emp._id)}
            className={`flex w-full items-center gap-2.5 px-3 py-2.5 text-left transition hover:bg-emerald-50/60 ${
              emp._id === value ? "bg-emerald-50" : ""
            }`}
          >
            <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white ${
              emp._id === value ? "bg-emerald-500" : "bg-gray-300"
            }`}>
              {emp.name?.[0] ?? "?"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-800 truncate">{emp.name}</p>
              {emp.designation && <p className="text-[11px] text-gray-400">{emp.designation}</p>}
            </div>
            {emp._id === value && (
              <span className="shrink-0 h-2 w-2 rounded-full bg-emerald-500" />
            )}
          </button>
        ))}
        <div ref={sentinel} className="h-1" />
        {loading && (
          <div className="flex items-center justify-center py-3 gap-2">
            <Loader2 size={14} className="animate-spin text-emerald-500" />
            <span className="text-xs text-gray-400">Loading…</span>
          </div>
        )}
      </div>
    </div>
  );
}

export function AllocateModal({ open, film, mode = "ALLOCATE", onClose, onDone }) {
  const { partner } = useAuth();
  const isChain = partner?.kind === "CHAIN";
  const isRevoke = mode === "REVOKE";
  const holderType = isChain ? "Partner" : "Employee";
  const [holder, setHolder] = useState("");
  const [keys, setKeys] = useState("1");
  const [note, setNote] = useState("");
  const [clientRef, setClientRef] = useState("");
  const [branches, setBranches] = useState([]);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setHolder("");
    setKeys("1");
    setNote("");
    setClientRef("");
    setErrors({});
    setFormError("");
    if (isChain) {
      listBranches({ limit: 100 })
        .then(res => setBranches(unwrapList(res).items))
        .catch(e => console.warn("branches load failed", e));
    }
  }, [open, isChain]);

  async function submit(e) {
    e.preventDefault();
    setErrors({});
    setFormError("");
    if (!holder) {
      setFormError(`Please select a ${holderType === "Partner" ? "branch" : "employee"}.`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        movie: film._id,
        holderType,
        holder,
        keys: Number(keys) || 1,
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(clientRef.trim() ? { clientRef: clientRef.trim() } : {}),
      };
      if (isRevoke) await revokeKeys(payload);
      else await allocateKeys(payload);
      onDone();
    } catch (err) {
      const mapped = fieldErrors(err);
      if (mapped) setErrors(mapped);
      else setFormError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isRevoke ? "Revoke keys" : "Allocate keys"}
      description={
        isRevoke
          ? `Pull keys back from a holder for "${film?.title ?? ""}".`
          : isChain
          ? `Hand keys to a branch or employee for "${film?.title ?? ""}".`
          : `Allocate keys to one of your employees for "${film?.title ?? ""}".`
      }
    >
      <form onSubmit={submit} className="space-y-4">
        {formError && (
          <div className="rounded-md bg-red-50 border border-red-200 px-3.5 py-3 text-sm text-red-700">
            {formError}
          </div>
        )}

        {isChain ? (
          /* CHAIN allocates to a branch */
          <Field label="Branch" required error={errors.holder}>
            <Select
              value={holder}
              onChange={e => setHolder(e.target.value)}
              className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
            >
              <option value="">Select branch…</option>
              {branches.map(b => (
                <option key={b._id} value={b._id}>{b.name}</option>
              ))}
            </Select>
          </Field>
        ) : (
          /* BRANCH allocates to their own employees */
          <div>
            <p className="mb-1.5 text-sm font-medium text-gray-700">
              Employee <span className="text-red-500">*</span>
            </p>
            {errors.holder && <p className="mb-1 text-xs text-red-500">{errors.holder}</p>}
            <EmployeePicker
              value={holder}
              onChange={setHolder}
              branchId=""
              isChain={false}
            />
          </div>
        )}

        <Field label="Number of keys" required error={errors.keys}>
          <Input
            value={keys}
            onChange={e => setKeys(e.target.value.replace(/\D/g, ""))}
            inputMode="numeric"
            placeholder="1"
            className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
          />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
          <Field label="Note" error={errors.note}>
            <Input
              value={note}
              onChange={e => setNote(e.target.value)}
              className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
            />
          </Field>
          <Field label="Reference" error={errors.clientRef}>
            <Input
              value={clientRef}
              onChange={e => setClientRef(e.target.value)}
              className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
            />
          </Field>
        </div>

        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2.5 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} className="text-gray-600 w-full sm:w-auto">
            Cancel
          </Button>
          <Button
            type="submit"
            loading={saving}
            className={`${isRevoke ? "bg-gradient-to-r from-red-500 to-red-600 hover:shadow-red-500/25" : "bg-gradient-to-r from-purple-500 to-purple-600 hover:shadow-purple-500/25"} text-white hover:shadow-lg w-full sm:w-auto`}
          >
            {isRevoke ? "Revoke keys" : "Allocate keys"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function ConsumeModal({ open, film, onClose, onDone }) {
  const [keys, setKeys] = useState("1");
  const [showDate, setShowDate] = useState("");
  const [showTime, setShowTime] = useState("");
  const [screen, setScreen] = useState("");
  const [note, setNote] = useState("");
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setKeys("1");
    setShowDate(new Date().toISOString().slice(0, 10));
    setShowTime("");
    setScreen("");
    setNote("");
    setErrors({});
    setFormError("");
  }, [open]);

  async function submit(e) {
    e.preventDefault();
    setErrors({});
    setFormError("");
    setSaving(true);
    try {
      const hasShow = showDate || showTime || screen.trim();
      await consumeKeys({
        movie: film._id,
        keys: Number(keys) || 1,
        ...(hasShow ? {
          show: {
            ...(showDate ? { date: showDate } : {}),
            ...(showTime ? { time: showTime } : {}),
            ...(screen.trim() ? { screen: screen.trim() } : {}),
          },
        } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      onDone();
    } catch (err) {
      const mapped = fieldErrors(err);
      if (mapped) setErrors(mapped);
      else setFormError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Consume keys"
      description={`Record a show playback for "${film?.title ?? ""}".`}
      maxWidth="max-w-md"
    >
      <form onSubmit={submit} className="space-y-4">
        {formError && (
          <div className="rounded-md bg-red-50 border border-red-200 px-3.5 py-3 text-sm text-red-700">
            {formError}
          </div>
        )}

        <Field label="Keys to consume" required error={errors.keys} hint="Usually 1 per show.">
          <Input
            value={keys}
            onChange={e => setKeys(e.target.value.replace(/\D/g, ""))}
            inputMode="numeric"
            placeholder="1"
            className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
          />
        </Field>

        <div className="grid grid-cols-3 gap-x-3">
          <Field label="Show date" error={errors["show.date"]}>
            <Input
              type="date"
              value={showDate}
              onChange={e => setShowDate(e.target.value)}
              className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
            />
          </Field>
          <Field label="Show time" error={errors["show.time"]}>
            <ClockTimePicker
              value={showTime}
              onChange={setShowTime}
            />
          </Field>
          <Field label="Screen" error={errors["show.screen"]}>
            <Input
              value={screen}
              onChange={e => setScreen(e.target.value)}
              placeholder="Screen 1"
              className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
            />
          </Field>
        </div>

        <Field label="Note" error={errors.note}>
          <Input
            value={note}
            onChange={e => setNote(e.target.value)}
            className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
          />
        </Field>

        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2.5 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} className="text-gray-600 w-full sm:w-auto">
            Cancel
          </Button>
          <Button
            type="submit"
            loading={saving}
            className="bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:shadow-lg hover:shadow-amber-500/25 w-full sm:w-auto"
          >
            Record show
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function ReturnModal({ open, film, onClose, onDone }) {
  const [keys, setKeys] = useState("1");
  const [note, setNote] = useState("");
  const [clientRef, setClientRef] = useState("");
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setKeys("1");
    setNote("");
    setClientRef("");
    setErrors({});
    setFormError("");
  }, [open]);

  async function submit(e) {
    e.preventDefault();
    setErrors({});
    setFormError("");
    setSaving(true);
    try {
      await returnKeys({
        movie: film._id,
        keys: Number(keys) || 1,
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(clientRef.trim() ? { clientRef: clientRef.trim() } : {}),
      });
      onDone();
    } catch (err) {
      const mapped = fieldErrors(err);
      if (mapped) setErrors(mapped);
      else setFormError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Return keys"
      description={`Return unused keys to the chain for "${film?.title ?? ""}".`}
      maxWidth="max-w-md"
    >
      <form onSubmit={submit} className="space-y-4">
        {formError && (
          <div className="rounded-md bg-red-50 border border-red-200 px-3.5 py-3 text-sm text-red-700">
            {formError}
          </div>
        )}

        <Field label="Number of keys" required error={errors.keys}>
          <Input
            value={keys}
            onChange={e => setKeys(e.target.value.replace(/\D/g, ""))}
            inputMode="numeric"
            placeholder="1"
            className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
          />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
          <Field label="Note" error={errors.note}>
            <Input
              value={note}
              onChange={e => setNote(e.target.value)}
              className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
            />
          </Field>
          <Field label="Reference" error={errors.clientRef}>
            <Input
              value={clientRef}
              onChange={e => setClientRef(e.target.value)}
              className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
            />
          </Field>
        </div>

        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2.5 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} className="text-gray-600 w-full sm:w-auto">
            Cancel
          </Button>
          <Button
            type="submit"
            loading={saving}
            className="bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-lg hover:shadow-emerald-500/25 w-full sm:w-auto"
          >
            Return keys
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function CloseFilmModal({ open, film, onClose, onDone }) {
  const isClose = film?.status !== "CLOSED";
  const [note, setNote] = useState("");
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setNote("");
    setFormError("");
  }, [open]);

  async function submit(e) {
    e.preventDefault();
    setFormError("");
    setSaving(true);
    try {
      await setMovieStatus(film._id, {
        status: isClose ? "CLOSED" : "ACTIVE",
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      onDone();
    } catch (err) {
      setFormError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isClose ? "Close film run" : "Reopen film run"}
      description={`"${film?.title ?? ""}"`}
      maxWidth="max-w-md"
    >
      <form onSubmit={submit} className="space-y-4">
        {formError && (
          <div className="rounded-md bg-red-50 border border-red-200 px-3.5 py-3 text-sm text-red-700">
            {formError}
          </div>
        )}

        <p className="text-sm text-gray-600">
          {isClose
            ? "Closing a run stops new key consumptions for this film. All ledger history is preserved."
            : "Reopening allows key consumptions to continue."}
        </p>

        <Field label="Note">
          <Input
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder={isClose ? "End of theatrical run" : ""}
            className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
          />
        </Field>

        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2.5 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} className="text-gray-600 w-full sm:w-auto">
            Cancel
          </Button>
          <Button
            type="submit"
            loading={saving}
            className={`${isClose ? "bg-gradient-to-r from-red-500 to-red-600" : "bg-gradient-to-r from-emerald-500 to-green-600"} text-white hover:shadow-lg w-full sm:w-auto`}
          >
            {isClose ? "Close film" : "Reopen film"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
